import { useEffect, useState } from 'react';
import { ProfileResponse, usePutProfilesMutation } from '../../store/backendApi';

interface UseProfileUpdateReturn {
  username: string;
  description: string;
  setUsername: (value: string) => void;
  setDescription: (value: string) => void;
  saveProfile: () => Promise<void>;
  confirmAndSave: () => Promise<void>;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  isUsernameChanging: boolean;
  needsConfirmation: boolean;
  setNeedsConfirmation: (value: boolean) => void;
}

/**
 * Hook to handle profile editing (username and description).
 * Platform-agnostic: Can be used in React Native.
 *
 * @param profile - The current profile data
 * @returns Profile form state, handlers, and save function
 */
export const useProfileUpdate = (profile?: ProfileResponse): UseProfileUpdateReturn => {
  const [username, setUsername] = useState('');
  const [description, setDescription] = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [updateProfile, { isLoading: isSaving }] = usePutProfilesMutation();

  // Sync local state with server profile data
  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setDescription(profile.description || '');
    }
  }, [profile]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = profile?.username !== username || profile?.description !== description;

  // Check if username is changing (requires confirmation)
  const isUsernameChanging = profile?.username !== username && profile?.username !== undefined;

  const performSave = async () => {
    // Only include fields that actually changed
    const updates: { username?: string; description?: string } = {};

    if (profile?.username !== username) {
      updates.username = username;
    }

    if (profile?.description !== description) {
      updates.description = description;
    }

    // Only send request if there are actual changes
    if (Object.keys(updates).length > 0) {
      await updateProfile(updates);
    }
  };

  const saveProfile = async () => {
    // If username is changing, require confirmation first
    if (isUsernameChanging) {
      setNeedsConfirmation(true);
      return;
    }

    // Otherwise, save directly
    await performSave();
  };

  const confirmAndSave = async () => {
    setNeedsConfirmation(false);
    await performSave();
  };

  return {
    username,
    description,
    setUsername,
    setDescription,
    saveProfile,
    confirmAndSave,
    isSaving,
    hasUnsavedChanges,
    isUsernameChanging,
    needsConfirmation,
    setNeedsConfirmation,
  };
};
